import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorNotification, PrimaryButton, Button } from '@cb/apricot-react';
import { CircularProgress, Grid } from '@mui/material';

import { deleteSchool, findOrgById, useMyAIForms } from 'src/apis';
import { AuthorizationLevelModal, AIFormCard, Typography } from 'src/components';
import { TAIForm, ISummary, ISubmitHistory, IAIUpdateRequest } from 'src/types';
import { APP_URLS } from 'src/constants';
import { useAuth } from 'src/contexts';
import { getFile } from 'src/services/schoolRequest';
import { UpdateForm } from 'src/components/school/UpdateForm';

interface IRecordView {
  summary: ISummary;
  request: IAIUpdateRequest;
}

const UserHome: FC = () => {
  const navigate = useNavigate();
  const { userName } = useAuth();

  const [viewing, setViewing] = useState<IRecordView | undefined>(undefined);
  const { data: aiForms, error, loading, loaded, fetch } = useMyAIForms({ lazy: false });

  const [school, setSchool] = useState<Nullable<ISummary>>(null);
  const [deleteError, setDeleteError] = useState<Nullable<string>>(null);
  const [fetchSchoolError, setFetchSchoolError] = useState<Nullable<string>>(null);
  const [isAuthLevelModalOpen, setIsAuthLevelModalOpen] = useState(false);

  const handleDeleteSchool = useCallback(
    async (school: ISummary) => {
      try {
        await deleteSchool(school.categoryId);
        setTimeout(fetch, 3000);
      } catch (error: any) {
        setDeleteError(error.message);
      }
    },
    [fetch],
  );

  const getRequest = async (summary: ISummary) => {
    if (summary.requestType === 'create') {
      if (!summary.draft) throw new Error("No submission could be located.");
      const aiForm: TAIForm = await getFile(summary.draft);
      return aiForm;
    } else {
      if (!summary.submission) throw new Error("No submission could be located.");
      const update: IAIUpdateRequest = await getFile(summary.submission);
      if (!update) throw new Error("Cannot find request");
      return update;
    }
  };

  const navigateToSchool = useCallback(
    async (school: ISummary, level?: string) => {
      try {
        if (school.appliedType === 'aia' && school.requestType === 'create') {
          navigate(APP_URLS.FORM, {
            state: {
              summary: school
            }
          });
        } else if (school.requestType === 'create') {
          if (!school.draft) throw new Error("No submission could be located.");
          const aiForm: TAIForm = await getFile(school.draft);
          navigate(APP_URLS.SCHOOL, {
            state: {
              school: {
                ...aiForm,
                materials: school.materials ?? [],
                processStatus: school.processStatus,
                requireTestingPlanUpdates: school.requireTestingPlanUpdates,
                appealEligibleUpdates: school.appealEligibleUpdates,
                aiCode: school.aiCode,
                ...(level ? { level } : {}),
              },
            },
          });
        } else {
          if (!school.submission) throw new Error("No submission could be located.");
          const update: IAIUpdateRequest = await getFile(school.submission);
          if (!update) throw new Error("Cannot find request");
          if (school.orgId) {
            // @DEPRECATED - This block is only need to allow opening old submissions
            // that didn't include the orgTypeCd originally, otherwise we can get all data
            // from the submission record
            if (!update.data?.orgTypeCd) {
              const record = await findOrgById(school.orgId);
              update.data.orgTypeCd = record.org.orgTypeCd;
            }
          }
          setViewing({
            summary: school,
            request: update
          });
        }
        setIsAuthLevelModalOpen(false);
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigate],
  );

  const handleClickUpdateAndSubmit = useCallback(
    async (school: ISummary) => {
      if (school.appliedType === 'aia' && school.requestType === 'create') {
        navigate(APP_URLS.FORM, {
          state: {
            summary: school
          }
        });
      } else {
        const request = await getRequest(school) as TAIForm;
        if (request.level) {
          navigateToSchool(school);
          setIsAuthLevelModalOpen(false);
        } else {
          setSchool(school);
          setIsAuthLevelModalOpen(true);
        }
      }
    },
    [navigateToSchool],
  );

  const handleCloseAuthorizationLevelModal = useCallback(() => {
    setIsAuthLevelModalOpen(false);
  }, []);

  const handleSubmitAuthorizationLevel = useCallback(
    async (level: string) => {
      if (!school) return;
      navigateToSchool(school, level);
    },
    [navigateToSchool, school],
  );

  const handleClickReSubmitSchool = useCallback(
    async (school: ISummary) => {
      try {
        await navigateToSchool(school);
        setIsAuthLevelModalOpen(false);
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigateToSchool],
  );

  const handleClickViewSchool = useCallback(
    async (school: ISummary) => {
      try {
        await navigateToSchool(school);
        setIsAuthLevelModalOpen(false);
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigateToSchool],
  );

  const handleClickViewSchoolSubmit = useCallback(
    async (school: ISummary, submit: ISubmitHistory) => {
      try {
        if (school.appliedType === 'aia' && school.requestType === 'create') {
          navigate(APP_URLS.FORM, {
            state: {
              summary: school,
              file: submit.submission
            }
          });
        } else if (school.requestType === 'create' && school.submission) {
          const aiForm: TAIForm = await getFile(school.submission);
          navigate(APP_URLS.SCHOOL, {
            state: {
              school: {
                ...aiForm,
                materials: school.materials ?? [],
                processStatus: school.processStatus,
                requireTestingPlanUpdates: school.requireTestingPlanUpdates,
                aiCode: school.aiCode,
              },
            },
          });
          setIsAuthLevelModalOpen(false);
        } else {
          if (!school.submission) throw new Error("No submission could be located.");
          const update: IAIUpdateRequest = await getFile(submit.submission);
          if (!update) throw new Error("Cannot find request");

          setViewing({
            summary: school,
            request: update
          });
        }
      } catch (error: any) {
        setFetchSchoolError(error.message);
      }
    },
    [navigate],
  );

  const handleRefresh = useCallback(() => {
    fetch();
  }, [fetch]);

  const handleClickStartNewRequest = useCallback(() => {
    navigate(APP_URLS.SEARCH);
  }, [navigate]);

  useEffect(() => {
    if (loaded && !aiForms.length) {
      navigate(APP_URLS.SEARCH);
    }
  }, [loaded, navigate, aiForms]);

  const errorMessage = error || deleteError || fetchSchoolError;

  return (
    <Grid container spacing={2}>
      {viewing ?
        <Grid item xs={12}>
          <div style={{ marginBottom: '10px' }}>
            <Button onClick={() => setViewing(undefined)}>Close</Button>
          </div>
          <UpdateForm onCancel={() => setViewing(undefined)} onComplete={(update) => { setViewing(undefined); fetch(); }} readOnly={viewing.summary.processStatus === 'submitted' ? false : true} request={viewing.request} />
        </Grid> :
        <>
          <AuthorizationLevelModal
            isOpen={isAuthLevelModalOpen}
            onClose={handleCloseAuthorizationLevelModal}
            onSubmit={handleSubmitAuthorizationLevel}
          />

          {loading && (
            <Grid container item xs={12} justifyContent="center">
              <CircularProgress color="secondary" />
            </Grid>
          )}

          {errorMessage && (
            // @ts-ignore
            <ErrorNotification dismissible title="Error">
              {errorMessage}
            </ErrorNotification>
          )}

          <Grid item xs={12}>
            <Typography variant="h4">Welcome, {userName}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1">
            Please allow approximately <b>7 -10 business days for processing for each submission </b>of your request.<br /> <b>Please note:</b> Please be sure to complete the form in its entirety and include any supporting documentation to expedite processing. Requests that require review by an additional vetting team will incur additional processing time. <b>The most up to date status of your application is displayed in the "Status" section below.</b>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <PrimaryButton style={{ width: '100%' }} onClick={handleClickStartNewRequest}>
              Search to start a request
            </PrimaryButton>
          </Grid>

          {aiForms.map((school) => (
            <Grid item xs={12} key={school.categoryId}>
              <AIFormCard
                school={school}
                onClickDelete={() => handleDeleteSchool(school)}
                onClickUpdateAndSubmit={() => handleClickUpdateAndSubmit(school)}
                onClickUpdateAndReSubmit={() => handleClickReSubmitSchool(school)}
                onClickView={() => handleClickViewSchool(school)}
                onClickViewSubmit={(submit) => handleClickViewSchoolSubmit(school, submit)}
                onRefresh={handleRefresh}
              />
            </Grid>
          ))}
        </>
      }
    </Grid>
  );
};

export default UserHome;
